import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Excel export hook - Filtrelenmiş verileri Excel dosyasına dönüştürür
 * 
 * @example
 * const { exportToExcel, isExporting } = useExportExcel();
 * 
 * exportToExcel({
 *   data: filteredChecks,
 *   columns: [
 *     { header: 'Çek No', key: 'checkNo' },
 *     { header: 'Tutar', key: 'amount', format: 'currency' },
 *     { header: 'Vade', key: 'dueDate', format: 'date' },
 *   ],
 *   fileName: 'cekler_raporu',
 *   sheetName: 'Çekler',
 * });
 */
export default function useExportExcel() {
    const [isExporting, setIsExporting] = useState(false);

    /**
     * Para birimini formatla
     */
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    /**
     * Tarihi formatla
     */
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    /**
     * Değeri formata göre dönüştür
     */
    const formatValue = (value, format) => {
        switch (format) {
            case 'currency':
                return formatCurrency(value);
            case 'date':
                return formatDate(value);
            case 'percent':
                return `%${value || 0}`;
            default:
                return value ?? '-';
        }
    };

    /**
     * Excel'e export et
     */
    const exportToExcel = useCallback(async ({
        data,
        columns,
        fileName = 'rapor',
        sheetName = 'Sayfa1',
        includeDate = true,
    }) => {
        if (!data || data.length === 0) {
            console.warn('Export için veri bulunamadı');
            return false;
        }

        setIsExporting(true);

        try {
            // Header satırı oluştur
            const headers = columns.map(col => col.header);

            // Veriyi dönüştür
            const rows = data.map(item => {
                return columns.map(col => {
                    const value = col.key.includes('.')
                        ? col.key.split('.').reduce((obj, key) => obj?.[key], item)
                        : item[col.key];
                    return formatValue(value, col.format);
                });
            });

            // Worksheet oluştur
            const wsData = [headers, ...rows];
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // Kolon genişliklerini ayarla
            const colWidths = columns.map((col, idx) => {
                const maxLength = Math.max(
                    col.header.length,
                    ...rows.map(row => String(row[idx] || '').length)
                );
                return { wch: Math.min(maxLength + 2, 50) };
            });
            ws['!cols'] = colWidths;

            // Workbook oluştur
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, sheetName);

            // Dosya adını formatla
            const dateStr = includeDate
                ? `_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}`
                : '';
            const fullFileName = `${fileName}${dateStr}.xlsx`;

            // Excel dosyasını oluştur ve indir
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, fullFileName);

            return true;
        } catch (error) {
            console.error('Excel export hatası:', error);
            return false;
        } finally {
            setIsExporting(false);
        }
    }, []);

    return {
        exportToExcel,
        isExporting,
        formatCurrency,
        formatDate,
    };
}
